/**
 * Swedish translations. Mirrors the keys in en.ts. Missing keys fall
 * back to English at runtime so a partial Swedish translation still
 * works — the user just sees a few English strings until we fill them in.
 */

import type { Translations } from "./en";

export const sv: Translations = {
  trial: {
    badge_active: "{days} dagar kvar i trial · {used}/{total} frågor",
    badge_nudge: "{remaining} frågor kvar — uppgradera för att fortsätta",
    badge_warning: "Trial snart slut · {remaining} frågor kvar",
    badge_blocked: "Trial slut — uppgradera för att fortsätta använda Ask Meridian",
    badge_paid: "Pro-plan",
    upgrade_button: "Uppgradera",
    quota_modal_title: "Du är fast — fortsätt nu",
    quota_modal_body: "Uppgradera ditt workspace för att ta bort trial-gränsen och låsa upp obegränsat med Ask Meridian, branchar och scenarier.",
  },

  common: {
    cancel: "Avbryt",
    save: "Spara",
    saving: "Sparar…",
    delete: "Radera",
    deleting: "Raderar…",
    confirm: "Bekräfta",
    back: "Tillbaka",
    next: "Nästa",
    skip: "Hoppa över",
    loading: "Laddar…",
    retry: "Försök igen",
    edit: "Redigera",
    rename: "Byt namn",
    open: "Öppna",
    add: "Lägg till",
    close: "Stäng",
    yes: "Ja",
    no: "Nej",
    error_generic: "Något gick fel",
    just_now: "nyss",
    minutes_ago: "{n} min sen",
    hours_ago: "{n} h sen",
    days_ago: "{n} d sen",
  },

  home: {
    scaffold_plan_link: "Hoppa tillbaka till din plan",
  },

  nav: {
    today: "Idag",
    home: "Hem",
    data: "Data",
    models: "Modeller",
    ask_meridian: "Ask Meridian",
    develop: "Utveckla",
    plans: "Planer",
    administration: "Administration",
  },

  data: {
    title: "Data",
    subtitle: "Ladda upp en fil — Meridian strukturerar den till en modell du kan ställa frågor mot.",
    upload: {
      drop_here: "Släpp en fil här",
      or_click: "eller klicka för att välja",
      uploading: "Laddar upp…",
      analyzing: "Analyserar…",
      hint_formats: "CSV, XLSX eller JSON · max {size} MB",
    },
    datasets_section: "Dataset",
    datasets_count: "{n} st",
    datasets_empty: "Inga dataset uppladdade än. Dra in en fil ovan för att komma igång.",
    delete_confirm: "Radera {name}?",
    status: {
      uploaded: "Uppladdat",
      analyzing: "Analyserar",
      ready: "Redo",
      failed: "Misslyckades",
    },
    clarify: {
      title: "Innan jag bygger modellen",
      reading_schema: "Läser ditt schema…",
      no_questions: "Schemat ser entydigt ut — jag kan bygga modellen direkt.",
      create_model: "Skapa modell",
      create_with_answers: "Skapa modell med dessa svar",
      add_to_existing: "Lägg till i modellen",
    },
    destination: {
      label: "Vart ska datan hamna?",
      new_model: "Ny modell",
      new_model_desc: "Bygg en helt ny modell från denna fil.",
      attach_existing: "Lägg till i befintlig",
      attach_existing_desc: "Stitcha ihop med en modell du redan byggt — Meridian föreslår kopplingarna.",
    },
    answer_placeholder: "Ditt svar…",
  },

  ask: {
    title: "Ask Meridian",
    subtitle_with_model: "Ställ en fråga mot {model}.",
    subtitle_without_model: "Ställ en fråga mot din modell.",
    placeholder: "Fråga vad som helst om din data…",
    placeholder_disabled: "Välj en branch först…",
    thinking: "Tänker…",
    pick_model: "Välj en modell att ställa frågor mot.",
    no_models: "Inga modeller än — gå till {data_link} och ladda upp en fil.",
    fetching_models: "Hämtar modeller…",
    asking_against: "frågar mot {model}",
    no_branch: "Kunde inte nå den aktiva modellen. Ladda om sidan eller kontrollera dess datakällor under Data.",
    suggestions: {
      revenue_q3: "Visa Q3 intäkt per region",
      forecast_quarter: "Vad blir omsättningen nästa kvartal?",
      what_if_price: "Vad händer om vi höjer priset 5%?",
      deals_march: "Vilka deals stänger sannolikt i mars?",
    },
    new_chat: "Ny chatt",
  },

  develop: {
    title: "Utveckla",
    subtitle: "Branchar, scenarier och merge requests per modell. Välj en modell för att öppna grafen.",
    no_models: "Inga modeller än",
    no_models_hint: "Ladda upp en fil i Data-tabben — Meridian skapar din första modell.",
    go_to_data: "Gå till Data",
    fetch_failed: "Kunde inte hämta modeller",
    created_at: "Skapad {when}",
    open_graph: "Öppna graf →",
    actions_label: "Modellåtgärder",
    rename_label: "Byt namn",
    rename_save: "Spara",
    delete_label: "Radera modell",
    delete_confirm_title: "Radera {name}?",
    delete_confirm_body: "Alla branchar, commits och artefakter under modellen försvinner. Det går inte att ångra.",
  },

  branches: {
    title: "Branchar",
    subtitle: "Live-modell med parallella dev-branchar. Klicka på en branch för detaljer.",
    fork_button: "Ny branch",
    loading: "Laddar branchar…",
    tab_branches: "Branchar",
    tab_sources: "Datakällor",
    tab_timeline: "Tidslinje",
    tab_insights: "Insikter",
  },

  sources: {
    title: "Datakällor",
    subtitle: "Källor som matar {model}. AI:n föreslår kopplingar — godkänn eller avvisa.",
    subtitle_no_model: "Källor som matar modellen.",
    add_button: "Lägg till källa",
    fetching: "Hämtar källor och kopplingar…",
    empty_title: "Inga datakällor än",
    empty_hint: "Klicka \"Lägg till källa\" eller ladda upp en fil i Data.",
    sidebar_sources: "Källor ({n})",
    sidebar_relationships: "Kopplingar ({n})",
    sidebar_no_rels: "Lägg till fler källor så hittar AI:n kopplingarna.",
    sidebar_click_hint: "Klicka på en linje i grafen för detaljer.",
    proposed_join: "Föreslagen koppling",
    confidence_label: "Confidence: {pct}%",
    accept: "Godkänn",
    reject: "Avvisa",
    accept_reject_hint: "Godkänn/avvisa kommer i nästa version — för nu visar Meridian alla föreslagna kopplingar.",
    dialog_title: "Lägg till datakälla",
    dialog_pick: "Välj dataset",
    dialog_no_available: "Inga lediga dataset i workspacet.\nLadda upp en fil i Data-tabben först.",
    dialog_attaching: "Kopplar…",
    dialog_attach: "Lägg till",
    fetching_datasets: "Hämtar dataset…",
  },
  chart_frame: {
    refine: "Förfina",
    confidence_medium_default: "Viss osäkerhet i diagrammet — bandet visar intervallet.",
    confidence_low_default: "Tunt dataunderlag — endast riktning. Ställ en mer specifik fråga för tightare resultat.",
    forecast_suppressed: "Prognos undertryckt — {reason}.",
    empty_zero_rows: "Inga data än. Prova ett av förslagen nedan.",
    empty_sparse: "Bara {count} datapunkter — visas som prickar, inte som trend.",
    sub_narrative_drill_hint: "Klicka på en understruken siffra för att borra ner.",
  },
};
